/**
 * Fibonacci Retracement Styles, Extension Modes, and Anchor Geometries.
 *
 * Provides level color mapping, backward-compatible extend mode resolution,
 * and day-end session projection calculations.
 */

export const DEFAULT_FIB_COLORS: Record<number, string> = {
  0: "#787B86",
  0.236: "#F23645",
  0.382: "#FF9800",
  0.5: "#4CAF50",
  0.618: "#089981",
  0.786: "#2962FF",
  1: "#787B86",
  1.618: "#9C27B0",
  2.618: "#F23645",
};

/** Standard trading session close offset in seconds (15:40 IST = 56,400s). */
export const SESSION_END_SEC = 15 * 3600 + 40 * 60;

export interface FibDrawingRecord {
  point1: { time: number; price: number };
  point2: { time: number; price: number };
  p3?: { time: number; price: number };
  extendMode?: string;
  extend?: boolean;
}

/**
 * Resolves Fibonacci extend mode, preserving backward compatibility for
 * legacy boolean `extend` or preset `'full'`/`'dayEnd'` values.
 */
export function getFibExtendMode(
  d: { extendMode?: string; extend?: boolean } | null | undefined,
): "off" | "custom" {
  if (!d) return "off";
  if (d.extendMode === "off" || d.extendMode === "custom") return d.extendMode;
  if (d.extendMode === "full" || d.extendMode === "dayEnd") return "custom";
  return d.extend === true ? "custom" : "off";
}

/**
 * Computes the anchor session's day-end target timestamp for the Fibonacci extension handle.
 */
export function computeFibDayEndTime(
  point1Time: number,
  point2Time: number,
  allCandles: Array<{ time: number }> | null | undefined,
  intervalSec: number,
  sessionEndSec: number = SESSION_END_SEC,
): number {
  const t = Math.max(point1Time, point2Time);
  const dayStart = Math.floor(t / 86400) * 86400;
  const dayEndTime = dayStart + sessionEndSec;

  if (allCandles && allCandles.length && allCandles[0].time <= dayEndTime) {
    let lo = 0;
    let hi = allCandles.length - 1;
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1;
      if (allCandles[mid].time <= dayEndTime) lo = mid;
      else hi = mid - 1;
    }
    const bar = allCandles[lo];
    if (bar.time >= dayStart) {
      const dayComplete =
        lo < allCandles.length - 1 ||
        bar.time - dayStart >= sessionEndSec - intervalSec;
      if (dayComplete) return bar.time;
    }
  }

  return dayEndTime;
}

/**
 * Ensures the Fibonacci drawing has an extend handle point (`p3`), computing a
 * fallback default if not yet placed.
 */
export function ensureFibP3(
  d: FibDrawingRecord,
  resolvedTime?: number | null,
  intervalSec: number = 60,
): { time: number; price: number } {
  if (d.p3) return d.p3;
  const time =
    resolvedTime != null
      ? resolvedTime
      : Math.max(d.point1.time, d.point2.time) + 20 * intervalSec;
  return { time, price: d.point2.price };
}
